import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {B2BUser} from '@spartacus/core';
import {DomSanitizer} from '@angular/platform-browser';
import {FeatureTogglesService} from '@dcp/configurator';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {filter, first} from 'rxjs/operators';
import {RestrictedAddTechContactCreationService} from './services/restricted-add-tech-contact-creation.service';
import {ComboBoxDropdownComponent, ComboBoxDropdownOptionComponent} from 'ng-design-system';
import {AddressUsage} from '@app/model/order/dcp-order-entry.model';
import {DcpCustomerRegistrationDecoratorService} from '@app/services/dcp-customer-registration-decorator.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Subject} from 'rxjs';
import {PermissionService} from '@app/services/permission/permission.service';

@UntilDestroy()
@Component({
  selector: 'dcp-technical-contacts',
  templateUrl: './technical-contacts.component.html',
  styleUrls: ['./technical-contacts.component.scss']
})
export class TechnicalContactsComponent implements OnInit {
  // add all new contacts created using DcpCustomerRegistrationService
  @Input() addExternalContacts = false;
  @Input() contacts: B2BUser[];
  @Input() header: string;
  @Input() initialSelectedContact: number = 0;
  @Input() newContactRole: AddressUsage;
  @Input() createNewContactText: string;
  @Input() editContactText: string;
  @Input() contactType: string;
  @Input() showEditButton: boolean = false;
  @Input() actionButtonAlternativeText: string = '';
  @Output() selectedContact: EventEmitter<B2BUser> = new EventEmitter<B2BUser>();
  @ViewChild('comboBox') comboBoxDropdown: ComboBoxDropdownComponent;
  @ViewChildren('dropdownOptions') dropdownOptions: QueryList<ComboBoxDropdownOptionComponent>;
  selectedContactData: string;
  openModal$: Subject<void> = new Subject();
  allowCreateNewContact: boolean = false;
  contactToEdit: B2BUser;
  openEditContactModal$: Subject<void> = new Subject();
  isContactEditable: boolean;

  constructor(
    private sanitizer: DomSanitizer,
    private togglesService: FeatureTogglesService,
    private restrictedAddTechContactService: RestrictedAddTechContactCreationService,
    private cdr: ChangeDetectorRef,
    private registrationService: DcpCustomerRegistrationDecoratorService,
    private permissionService: PermissionService,
  ) {
    this.subscribeToAllNewContacts();
  }

  ngOnInit(): void {
    this.subscribeToRestrictedAddTechContact();
    this.subscribeToCheckoutDataEditFeatureToggle();
    if (this.isSingleItContact()) {
      this.selectContacts([this.contacts[0].uid]);
    }
  }

  subscribeToRestrictedAddTechContact() {
    this.restrictedAddTechContactService.get().pipe(untilDestroyed(this)
    ).subscribe((value) => {
      this.allowCreateNewContact = value && this.permissionService.hasPermission('contact-creation');
      this.cdr.detectChanges();
    });
  };

  private subscribeToCheckoutDataEditFeatureToggle() {
    this.togglesService.isEnabled(UNLEASH_TOGGLES.checkoutEditAddress)
      .pipe(first(), untilDestroyed(this))
      .subscribe(enabled => this.isContactEditable = enabled);
  }

  isSingleItContact(): boolean {
    return this.contacts?.length === 1;
  }

  selectContacts(contactUid: string[]): void {
    const selectedContact: B2BUser = this.contacts.find(contact => contact.uid === contactUid[0]);
    this.selectedContact.emit(selectedContact);
    this.selectedContactData = this.sanitizer.bypassSecurityTrustHtml(`<div><span data-cy="contact__name">${selectedContact.name}</span><br/>
    <span data-cy="contact__email" class="ds-fine-print">${selectedContact.email}</span></div>`) as string;
  }

  addNewContact(contact: B2BUser): void {
    if (!this.contacts.includes(contact)) {
      this.contacts = [].concat(contact, this.contacts);
    }
  }

  openModal(): void {
    this.openModal$.next();
  }

  subscribeToAllNewContacts() {
    this.registrationService.getNewlyRegisteredContacts().pipe(
      filter(() => this.addExternalContacts), // update contacts only if flag is set
      untilDestroyed(this),
    ).subscribe((contact) => {
      this.addNewContact(contact);
    });
  }

  onEditClick(contact: B2BUser) {
    this.contactToEdit = contact;
    this.openEditContactModal$.next();
  }

  updateContact(updatedContact: B2BUser) {
    const updatedContactIndex = this.contacts.findIndex(contact => contact.uid === updatedContact.uid);
    this.contacts.splice(updatedContactIndex, 1, updatedContact);
    this.cdr.detectChanges();
    const selectedDropdownOption = this.dropdownOptions.find(contact => contact.value === updatedContact.uid);
    if (selectedDropdownOption && this.comboBoxDropdown) {
      this.comboBoxDropdown.setValue([updatedContact.uid]);
    }
    this.cdr.detectChanges();
  }
}
